import {
    Alert,
    AlertTitle,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import React, {useEffect, useState} from "react";
import {LoadingButton} from "@mui/lab";
import {useMutation} from "react-query";
import {Folio, FolioPayment, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import ProviderFactory from "./providers/ProviderFactory";
import {AmountType} from "crqlar-react-common/dist/models/common/amountType.model";
import {useAvailableBillingProviders} from "crqlar-react-common/dist/utils/hooks/billing/useAvailableBillingProviders";
import {useSnackbar} from 'notistack'

interface Props extends DialogProps {
    folio: Folio;
    total?: AmountType;
    onClose: () => void;
    onFail: () => void;
    onSuccess?: (folio: Folio) => void;
}

const FolioChargeDialog = (props: Props) => {
    const {t} = useTranslation();
    const {folio, total, onClose, onSuccess, onFail, ...dialogProps} = props;

    const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
    const [providerData, setProviderData] = useState<any>(null);
    const [isProviderDataValid, setIsProviderDataValid] = useState(false);
    const [amount, setAmount] = useState<AmountType | undefined>(total);
    const [selectedProfileId, setSelectedProfileId] = useState<string | undefined>(folio.profileId);
    // Persistent failure shown inside the dialog (the snackbar auto-dismisses too fast to read).
    const [chargeError, setChargeError] = useState<string | null>(null);
    const [chargeErrorDetail, setChargeErrorDetail] = useState<string | null>(null);
    const {enqueueSnackbar} = useSnackbar()

    const providersQuery = useAvailableBillingProviders();
    const providers = providersQuery.data || [];

    // Reset provider selection when dialog opens/closes and store the amount
    useEffect(() => {
        if (props.open) {
            setSelectedProvider(null);
            setProviderData(null);
            setIsProviderDataValid(false);
            setAmount(total);
            setSelectedProfileId(folio.profileId);
            setChargeError(null);
            setChargeErrorDetail(null);
        }
    }, [props.open, total, folio.profileId]);

    const handleProviderDataChange = (isValid: boolean, data: any) => {
        setIsProviderDataValid(isValid);
        setProviderData(data);
    };

    const chargeMutation = useMutation(
        'chargeFolio',
        async () => {
            if (!selectedProvider) return;

            const payment: FolioPayment = {
                provider: selectedProvider,
                amount: amount,
                transactionDate: new Date().toISOString().split('T')[0],
                // Include provider-specific data
                paymentMethodId: providerData?.paymentMethodId,
                email: providerData?.email,
                firstName: providerData?.firstName,
                lastName: providerData?.lastName,
                sendEmail: providerData?.sendEmail,
                chargeImmediately: providerData?.chargeImmediately,
                roomId: providerData?.roomId,
                externalUserId: providerData?.externalUserId,
                profileId: providerData?.profileId,
                reservationId: providerData?.reservationId
            };

            const response = await FolioService.chargeFolio({
                id: folio.id as string,
                payment
            });

            if (response && response.status === FolioStatus.OPEN) {
                // Charging failed, folio is still in OPEN state
                let errorMessage = t('Charging failed');
                let errorDetail: string | null = null;

                if (selectedProvider === 'asa' && response.folioProviderData) {
                    try {
                        const providerData = JSON.parse(response.folioProviderData);
                        if (providerData.success === false && providerData.error?.value) {
                            errorMessage = providerData.error.value;
                        }
                    } catch (e) {
                        console.error('Failed to parse folioProviderData:', e);
                    }
                }

                if (selectedProvider === 'casablanca' && response.folioProviderData) {
                    try {
                        const providerData = JSON.parse(response.folioProviderData);
                        if (providerData.success === false && providerData.error) {
                            // providerData.error is an i18n key (e.g. "casablanca.guest.not.checked.in")
                            errorMessage = t(providerData.error) || providerData.error;
                            // providerData.message carries the raw server detail (useful for 4xx)
                            if (providerData.message) errorDetail = providerData.message;
                        }
                    } catch (e) {
                        console.error('Failed to parse casablanca folioProviderData:', e);
                    }
                }

                setChargeError(errorMessage);
                setChargeErrorDetail(errorDetail);
                enqueueSnackbar(errorMessage, {variant: 'error'})
                onFail()
            } else {
                // Success - folio status changed
                setChargeError(null);
                setChargeErrorDetail(null);
                enqueueSnackbar(t("Charge success"), {variant: 'success'});
                response && onSuccess?.(response);
                onClose();
            }
        }
    );

    const handleCharge = () => {
        setChargeError(null);
        setChargeErrorDetail(null);
        chargeMutation.mutate();
    };

    const canCharge = selectedProvider && isProviderDataValid && !chargeMutation.isLoading;

    return (
        <Dialog
            {...dialogProps}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {t('Charge Folio')}
            </DialogTitle>

            <DialogContent>
                <Stack spacing={3} sx={{mt: 1}}>

                    {chargeError && (
                        <Alert severity="error" onClose={() => setChargeError(null)}>
                            {/* Headline = the specific reason; fall back to the generic label only
                                when no provider-specific message could be extracted. */}
                            <AlertTitle>{chargeError}</AlertTitle>
                            {chargeErrorDetail || t('Please try again or contact support.')}
                        </Alert>
                    )}

                    <Typography variant="body1">
                        {t('Folio')}: #{folio.id} - {folio.name || ''}
                    </Typography>

                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                        <Typography variant="body1" gutterBottom>
                            {t('Amount to charge')}:
                        </Typography>
                        <TextField
                            type="number"
                            value={amount?.amount || 0}
                            onChange={(e) => {
                                const newAmount = parseFloat(e.target.value);
                                if (!isNaN(newAmount)) {
                                    setAmount({
                                        ...amount,
                                        amount: newAmount,
                                        amountCents: newAmount * 100
                                    });
                                }
                            }}
                            InputProps={{
                                startAdornment: <InputAdornment
                                    position="start">{amount?.currency || '€'}</InputAdornment>,
                            }}
                        />
                    </Stack>

                    <Divider/>

                    <FormControl sx={{alignSelf: 'flex-start'}}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('Select Payment Provider')}
                        </Typography>

                        <ToggleButtonGroup
                            value={selectedProvider}
                            exclusive
                            onChange={(_, value) => setSelectedProvider(value)}
                            aria-label="payment provider"
                            fullWidth
                            color="primary"
                            sx={{
                                flexWrap: 'wrap',
                                '& .MuiToggleButtonGroup-grouped': {
                                    margin: 0.5,
                                    flex: 1,
                                    alignSelf: 'flex-start',
                                    minWidth: '120px',
                                    border: '1px solid !important',
                                    borderRadius: '4px !important',
                                },
                            }}
                        >
                            {providersQuery.isLoading ? (
                                <Box sx={{p: 2, textAlign: 'center', width: '100%'}}>
                                    <Typography variant="body2">{t('Loading providers...')}</Typography>
                                </Box>
                            ) : providers.length > 0 ? (
                                providers.map((provider) => (
                                    <ToggleButton
                                        key={provider}
                                        value={provider}
                                        sx={{
                                            textTransform: 'none',
                                            py: 1
                                        }}
                                    >
                                        {provider}
                                    </ToggleButton>
                                ))
                            ) : (
                                <Box sx={{p: 2, textAlign: 'center', width: '100%'}}>
                                    <Typography variant="body2">{t('No payment providers available')}</Typography>
                                </Box>
                            )}
                        </ToggleButtonGroup>

                        {providersQuery.isError && (
                            <FormHelperText error>
                                {t('Failed to load payment providers')}
                            </FormHelperText>
                        )}
                    </FormControl>

                    {selectedProvider && (
                        <Box sx={{mt: 2}}>
                            <Divider sx={{mb: 2}}/>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('Provider Details')}
                            </Typography>

                            <ProviderFactory
                                provider={selectedProvider}
                                profileId={selectedProfileId}
                                folio={folio}
                                onDataChange={handleProviderDataChange}
                            />
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={handleCharge}
                    disabled={!canCharge}
                    loading={chargeMutation.isLoading}
                >
                    {t('Charge')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default FolioChargeDialog;